import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormSteps } from "../../hooks/useFormSteps";
import Stepper from "../../components/ui/Stepper";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import DatePicker from "../../components/ui/DatePicker";
import Loader from "../../components/ui/Loader";
import FileUploader from "../../components/ui/FileUploader";
import LocationSelector from "../../components/ui/LocationSelector";
import SkillSelector from "../../components/ui/SkillSelector";
import { UserProfileFormData } from "../../types/components";
import { useAuth } from "../../hooks/useAuth";
import { useJobSeeker } from "../../context/JobSeekerContext";
import allCities from "../../config/allCities.json";
import { CityObj, Qualification, SkillObj } from "../../config/metadata";
import {
  AddJobSeekerDetailsRequest,
  Attachments,
  Gender,
} from "../../types/api";
import {
  mapApiToFormData,
  mapSelectedSkillsToApi,
  mapPreferredLocationsToApi,
  mapApiToSelectedSkill,
  JobseekerSkillObj,
} from "../../utils/jobSeekerProfileMapper";
import {
  mapskills,
  resolveCourseName,
  resolveQualificationName,
} from "../../utils/jobPostMapper"; // Import resolveCourseName, resolveQualificationName
import { SendOtpRequest, UpdateEmailRequest } from "../../types/api"; // Import SendOtpRequest
import qualificationsData from "../../config/allQualifications.json"; // Import qualificationsData
import Select from "../../components/ui/Select"; // Import Select component
import userService from "../../services/user.service"; // Import userService
import Toast from "../../components/ui/Toast";
import { deepEqual } from "../../utils/helpers";

// Helper function to format number to INR currency
const formatCurrencyINR = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const num = Number(String(value).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) {
    return "";
  }
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(num);
};

// Helper function to parse INR currency string to number
const parseCurrencyINR = (value: string): number => {
  if (!value) {
    return 0;
  }
  const cleanedValue = value.replace(/[^0-9.]/g, ''); // Remove all non-numeric characters except decimal
  const num = parseFloat(cleanedValue);
  return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
};

const UserProfileForm = () => {
  const navigate = useNavigate();
  const { user, verifyEmailOTP, updateUser } = useAuth(); // Removed sendOTP from here
  const {
    isOnboardingPaymentDone,
    paymentLoading,
    addJobSeekerDetails,
    jobSeekerDetails,
    updateJobSeekerAttachments,
  } = useJobSeeker();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<{ id: string; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: string; name: string }[]>([]);
  const [isEmailOtpSent, setIsEmailOtpSent] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState<string | null>(null);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);

  // Step 2 - Qualifications
  type EducationRow = {
    qualificationId: number;
    courseId: number | null;
    courseName: string | null;
    customCourse: string | null;
    allowCustomInput: boolean;
  };
  const [educationRows, setEducationRows] = useState<EducationRow[]>([
    {
      qualificationId: 0,
      courseId: null,
      courseName: null,
      customCourse: null,
      allowCustomInput: false,
    },
  ]);
  const courseList: Map<number, [{ value: string; label: string }]> =
    React.useMemo(() => {
      const map = new Map();
      (qualificationsData as Qualification[]).forEach((q) => {
        map.set(
          q.qualificationId,
          q.courses.map((c) => {
            return { value: String(c.courseId), label: c.courseName };
          })
        );
      });
      return map;
    }, []);
  // Sorted qualifications for consistent dropdown ordering
  const sortedQualifications = React.useMemo(() => {
    const desiredOrder = [
      "Doctorate/PhD",
      "Masters/Post-Graduation",
      "Graduation/Diploma",
      "12th",
      "10th",
      "Below 10th",
    ];
    const orderIndex = (name: string) => {
      const idx = desiredOrder.indexOf(name);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...(qualificationsData as any[])].sort(
      (a, b) =>
        orderIndex(a.qualificationName) - orderIndex(b.qualificationName)
    );
  }, []);

  // Step 3 - Experience
  const [yearsOfExperience, setYearsOfExperience] = useState<number | "">("");
  // Step 4 - Skills & Locations
  const [selectedSkills, setSelectedSkills] = useState<JobseekerSkillObj[]>([]);
  // const [preferredLocationIds, setPreferredLocationIds] = useState<CityObj[]>([]);

  const steps = [
    { title: "Onboarding", description: "Complete Payment & Onboarding" },
    { title: "Basic Info", description: "Your core persona" },
    { title: "Education Info", description: "Your educational qualifications" },
    { title: "Work / Experience", description: "Work / Experience details" },
    { title: "Skills & Locations", description: "Skills and locations" },
    { title: "Complete", description: "Review and finish" },
  ];

  const initialUserProfileFormData: UserProfileFormData = {
    fullName:
      (user as any)?.firstName || (user as any)?.lastName
        ? `${(user as any)?.firstName || ""} ${
            (user as any)?.lastName || ""
          }`.trim()
        : (user as any)?.name || "",
    experienceType: undefined,
    expFile: null,
    preferredRole: "",
    currentCTC: "",
    attachments: [],
    emailOtp: "",
    emailVerified: false,
    experience: [], // Assuming ExperienceEntry[] based on usage in UserProfileForm.tsx
    firstName: "",
    lastName: "",
    email: "",
    phone: (user as any)?.mobileNumber || (user as any)?.phone || "",
    dateOfBirth: (user as any)?.dateOfBirth || "",
    gender: "",
    address: "",
    skills: [],
    jobTypes: [],
    workModes: [],
    preferredLocations: [],
    qualifications: [],
  };

  const {
    currentStep,
    formData,
    nextStep,
    prevStep,
    goToStep,
    updateFormData,
    isFirstStep,
    isLastStep,
  } = useFormSteps<UserProfileFormData>(steps, initialUserProfileFormData);

  const location = useLocation();

  // Effect to set initial step from URL query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const stepParam = queryParams.get("step");
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 0 && stepNumber < steps.length) {
        if(!isOnboardingPaymentDone){
           navigate("/user/profile/complete?step=0");
        }
        goToStep(stepNumber);
      }
    }
  }, [location.search, steps.length]);

  // Effect to prefill form data from jobSeekerDetails
  useEffect(() => {
    console.log({ formData });
  }, [formData]);
  useEffect(() => {
    if (jobSeekerDetails && user) {
      const mappedData = mapApiToFormData(jobSeekerDetails, user);
      // Format currentCTC from API data
      if (mappedData.currentCTC) {
        mappedData.currentCTC = formatCurrencyINR(mappedData.currentCTC);
      }
      console.log({ mappedData, jobSeekerDetails });
      updateFormData(mappedData);

      // Set selected qualifications
      if (jobSeekerDetails.jobSeekerQualificationDetails != undefined) {
        setEducationRows((prev) => {
          const qs: EducationRow[] =
            jobSeekerDetails.jobSeekerQualificationDetails
              ? jobSeekerDetails.jobSeekerQualificationDetails.map((jsq) => {
                  // Find the corresponding qualificationId for the courseId
                  let qualificationId: number = 0;
                  let allowCustomInput = false;
                  let courseName: string | null = null;
                  for (const qData of qualificationsData) {
                    const course = qData.courses.find(
                      (c) => c.courseId === jsq.courseId
                    );
                    if (course) {
                      qualificationId = qData.qualificationId;
                      allowCustomInput = course.allowCustomInput;
                      courseName = course.courseName;
                      break;
                    }
                  }
                  return {
                    qualificationId: qualificationId,
                    courseId: jsq.courseId,
                    courseName: courseName,
                    customCourse: jsq.customCourseName,
                    allowCustomInput: allowCustomInput,
                  };
                })
              : [];
          return [
            ...qs,
            {
              qualificationId: 0,
              courseId: null,
              courseName: null,
              customCourse: null,
              allowCustomInput: false,
            },
          ];
        });
      }

      // Set years of experience
      if (
        mappedData.experienceType === "experienced" &&
        jobSeekerDetails.jobSeekerExperienceDetails?.yearsOfExperience !==
          undefined
      ) {
        setYearsOfExperience(
          jobSeekerDetails.jobSeekerExperienceDetails.yearsOfExperience
        );
      }

      // Set selected skills
      if (jobSeekerDetails.jobSeekerSkillDetails) {
        const mappedSkills = mapApiToSelectedSkill(
          jobSeekerDetails.jobSeekerSkillDetails
        );
        setSelectedSkills(mappedSkills);
      }
    }
  }, [jobSeekerDetails, user]);

  const handleAttachmentUpload = (newAttachment: Attachments | null) => {
    if (!newAttachment || !newAttachment.typeId) {
      console.error(
        "Uploaded attachment is missing or missing typeId:",
        newAttachment
      );
      setToast({ message: "Failed to upload file", type: "error" });
      return;
    }

    // Update companyDetails in context
    updateJobSeekerAttachments(newAttachment);
  };
  // Load master data for Step 1
  useEffect(() => {
    // Build states list from allCities.json
    try {
      const stateSet = new Set<string>();
      const stateList: { id: string; name: string }[] = [];
      allCities.forEach((c) => {
        const stateName = c.stateName;
        if (stateName && !stateSet.has(stateName)) {
          stateSet.add(stateName);
          stateList.push({ id: stateName, name: stateName });
        }
      });
      stateList.sort((a, b) => a.name.localeCompare(b.name));
      setStates(stateList);
    } catch {
      setStates([]);
      setCities([]);
    }
  }, []);
  useEffect(() => {
    const filtered = allCities
      .filter((c) =>
        formData.currentState ? c.stateName == formData.currentState : true
      )
      .map((c) => ({ id: String(c.cityId), name: c.cityName }));
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    setCities(filtered);
  }, [formData.currentState]);
  // When user data loads later, backfill name/phone if empty
  useEffect(() => {
    if (!user) return;
    const derivedName = `${user?.firstName || ""} ${
      user?.lastName || ""
    }`.trim();
    const derivedPhone = user?.mobileNumber;
    updateFormData({
      fullName: derivedName,
      phone: derivedPhone,
    });
  }, [user]);

  const handleStateChange = async (stateId: string) => {
    updateFormData({
      ...(formData as any),
      currentState: stateId,
      currentCity: "",
    });
  };

  // Step 2 helpers
  const isRowComplete = (row: EducationRow) => {
    if (!row.qualificationId) return false;
    return row.courseId != null;
  };

  const ensureTrailingEmptyRow = (rows: EducationRow[]): EducationRow[] => {
    const last = rows[rows.length - 1];
    if (isRowComplete(last)) {
      return [
        ...rows,
        {
          qualificationId: 0,
          courseId: null,
          courseName: null,
          customCourse: null,
          allowCustomInput: false,
        },
      ];
    }
    return rows;
  };

  const updateRow = (
    index: number,
    updater: (r: EducationRow) => EducationRow
  ) => {
    setEducationRows((prev) => {
      const copy = prev.map((r, i) => (i === index ? updater(r) : r));
      return ensureTrailingEmptyRow(copy);
    });
  };

  const removeRow = (index: number) => {
    setEducationRows((prev) => {
      const copy = prev.filter((_, i) => i !== index);
      if (copy.length === 0)
        return [
          {
            qualificationId: 0,
            courseId: null,
            courseName: null,
            customCourse: null,
            allowCustomInput: false,
          },
        ];
      return copy;
    });
  };

  // Step 4 helpers
  const removeSkill = (id: number) =>
    setSelectedSkills((prev) => prev.filter((s) => s.id !== id));

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        // Block moving forward until payment done
        if (!isOnboardingPaymentDone) {
          setToast({
            message: "Please complete payment to proceed.",
            type: "warning",
          });
          return;
        }
      } else if (currentStep === 1) {
        // Gender mandatory and submit to onboard
        if (!formData.gender) {
          setToast({ message: "Please select gender", type: "error" });
          return;
        }
        const newPayload = {
          gender: formData.gender,
          address: formData.address,
          cityName: formData.currentCity?.split(",")[1],
          cityId: formData.currentCity?.split(",")[0]
            ? parseInt(formData.currentCity?.split(",")[0])
            : undefined,
        };

        if (!deepEqual(jobSeekerDetails?.jobSeekerBasicDetails, newPayload)) {
          setLoading(true);
          const payload: AddJobSeekerDetailsRequest = {
            userId: user?.userId,
            jobSeekerBasicDetails: newPayload,
          };
          await addJobSeekerDetails(payload);
        }
      } else if (currentStep === 2) {
        // Step 2 payload
        const incompleteRows: EducationRow[] = educationRows.filter((row) => row.courseId == null)
        if(incompleteRows.length > 0 && incompleteRows[0].qualificationId != 0){
          throw new Error("missing course details for few qualifications")
        }
        const newBody = educationRows
          .filter((row) => row.courseId !== null)
          .map((row) => ({
            courseId: row.courseId!,
            courseName: row.courseName,
            customCourseName: row.customCourse || null,
            isCompleted: null,
          }));

        if (
          !deepEqual(jobSeekerDetails?.jobSeekerQualificationDetails, newBody)
        ) {
          setLoading(true);
          const payload: AddJobSeekerDetailsRequest = {
            userId: user?.userId,
            jobSeekerQualificationDetails: newBody,
          };
          await addJobSeekerDetails(payload);
        }
      } else if (currentStep === 3) {
        // Step 3 payload
        const isFresher = formData.experienceType !== "experienced";
        const expYears = isFresher ? undefined : Number(yearsOfExperience || 0);
        if (!isFresher && (!expYears || expYears < 1 || expYears > 60)) {
          setToast({
            message: "Please enter years of experience between 1 and 60",
            type: "warning",
          });
          return;
        }
        const newBody = {
          isFresher,
          yearsOfExperience: expYears,
          salaryCurrency: null, // Assuming currency is always INR for now
          currentCTC: parseCurrencyINR(formData.currentCTC || "").toString(),
        };
        const oldData = jobSeekerDetails?.jobSeekerExperienceDetails;
        if (
          (newBody.isFresher && !oldData?.isFresher) ||
          (!newBody.isFresher &&
            (newBody.currentCTC != oldData?.currentCTC ||
              newBody.yearsOfExperience != oldData?.yearsOfExperience))
        ) {
          setLoading(true);
          const payload: AddJobSeekerDetailsRequest = {
            userId: user?.userId,
            jobSeekerExperienceDetails: newBody,
          };
          await addJobSeekerDetails(payload);
        }
      } else if (currentStep === 4) {
        // Step 4 payload
        const newSkills = mapSelectedSkillsToApi(selectedSkills);
        const newPreferedLocation = mapPreferredLocationsToApi(
          formData.preferredLocations
        );
        if (
          !deepEqual(jobSeekerDetails?.jobSeekerSkillDetails, newSkills) ||
          !deepEqual(
            jobSeekerDetails?.jobSeekerPreferredLocationDetails,
            newPreferedLocation
          )
        ) {
          setLoading(true);
          const payload: AddJobSeekerDetailsRequest = {
            userId: user?.userId,
            jobSeekerSkillDetails: newSkills,
            jobSeekerPreferredLocationDetails: newPreferedLocation,
          };
          await addJobSeekerDetails(payload);
        }
        // navigate("/user/dashboard");
      }

      if (currentStep < steps.length - 1) {
        nextStep();
      }
    } catch (e: any) {
      console.log(">>",e.message)
      setToast({
        message:
          (typeof e == "object" && (e.data || e.message)) ||
          (typeof e == "string" ? e : "something went wrong"),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!user?.userId || !formData.email) {
      setEmailOtpError("User ID or email is missing.");
      return;
    }
    setEmailOtpLoading(true);
    setEmailOtpError(null);
    try {
      const emailUpdateRequest: UpdateEmailRequest = {
        email: formData.email,
      };
      await userService.updateUserEmail(emailUpdateRequest); // Call updateUserEmail
      setIsEmailOtpSent(true);
    } catch (error: any) {
      console.error("Error updating email and sending OTP:", error);
      setEmailOtpError(
        error.data ||
          error.message ||
          "Failed to update email and send OTP."
      );
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!user?.userId || !formData.email || !formData.emailOtp) {
      setEmailOtpError("User ID, email, or OTP is missing.");
      return;
    }
    setEmailOtpLoading(true);
    setEmailOtpError(null);
    try {
      const otpData = {
        userId: user.userId,
        email: formData.email,
        otp: formData.emailOtp,
        type: "Email",
        requestType: "VerifyEmail",
      };
      const success = await verifyEmailOTP(otpData);
      if (success) {
        updateFormData({ emailVerified: true });
        updateUser({ emailVerified: true, email: formData.email }); // Update user context
      } else {
        setEmailOtpError("Invalid OTP.");
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      setEmailOtpError(error.message || "Failed to verify OTP.");
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      prevStep();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Final step submit: skills and preferred locations
      const payload: AddJobSeekerDetailsRequest = {
        userId: user?.userId,
        isProfileComplete: true,
      };
      await addJobSeekerDetails(payload);
      navigate("/user/dashboard");
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Step 1: Onboarding
            </h3>
            <div className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen rounded-xl p-4 text-primary-black">
              {!paymentLoading && !isOnboardingPaymentDone && (
                <div className="mb-4 p-3 bg-white/20 rounded-lg border border-white/30">
                  <p className="mb-3 font-medium">
                    Welcome! Complete payment to unlock placement services, then
                    proceed to complete your profile.
                  </p>
                  <p className="text-sm font-semibold text-primary-black">
                    <ul>
                      <li>Get placed in 6 months or get your ₹199 back</li>
                      <li> No hidden fees.</li>
                      <li> No false promises.</li>
                      <li> Just real opportunities.</li>
                    </ul>
                  </p>
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                {(isOnboardingPaymentDone && !paymentLoading ) ? (
                  <>
                    <Button
                      loading={paymentLoading}
                      variant="outline"
                      onClick={() => goToStep(1)}
                      disabled={!isOnboardingPaymentDone}
                      className="bg-white text-primary-black border-white hover:bg-gray-100"
                    >
                      Complete Profile
                    </Button>
                    {(user?.role != 'Intern' && user?.role != 'Jobseeker')&& <Button
                      loading={paymentLoading}
                      variant="outline"
                      disabled={isOnboardingPaymentDone}
                      className="bg-white text-primary-black border-white cursor-default disabled:text-primary-black disabled:bg-white"
                    >
                      Payment Completed
                    </Button>}
                  </>
                ) : (
                  <Button
                    loading={paymentLoading}
                    variant="outline"
                    className="bg-white text-black border-white hover:bg-gray-100"
                    onClick={() => navigate("/user/payment")}
                  >
                    Proceed to Payment
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Step 1: Basic Info
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                name="fullName"
                value={formData.fullName || ""}
                onChange={(e) => updateFormData({ fullName: e.target.value })}
                disabled={true}
                required
              />
              <Input
                label="Date of Birth"
                name="fullName"
                value={formData.dateOfBirth || ""}
                // onChange={(val) => updateFormData({ dateOfBirth: val })}
                disabled={true}
                required
              />
              {/* <DatePicker
                label="Date of Birth"
                value={formData.dateOfBirth || ""}
                onChange={(val) => updateFormData({ dateOfBirth: val })}
                // disabled={true}
                required
              /> */}
              <div className="w-full space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  value={formData.gender || ""}
                  onChange={(e) =>
                    updateFormData({ gender: e.target.value as Gender })
                  }
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white"
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Other</option>
                </select>
              </div>
              <Input
                label="Mobile Number"
                name="phone"
                value={formData.phone || ""}
                onChange={(e) => updateFormData({ phone: e.target.value })}
                disabled={true}
                required
              />
              {/* Current CTC field removed per requirement */}
              <div className="w-full space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={formData.currentState || ""}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <select
                  value={formData.currentCity || ""}
                  disabled={!formData.currentState}
                  onChange={(e) =>
                    updateFormData({
                      ...formData,
                      currentCity: e.target.value,
                    } as any)
                  }
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white disabled:bg-gray-200 disabled:text-gray-400"
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c.id} value={`${c.id},${c.name}`}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (OTP Verification)
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData({ email: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                    placeholder="you@example.com"
                    disabled={isEmailOtpSent || formData?.emailVerified}
                  />
                  {!formData.emailVerified && (
                    <Button
                      variant="outline"
                      onClick={handleSendOtp}
                      loading={emailOtpLoading}
                      disabled={isEmailOtpSent || formData?.emailVerified}
                    >
                      {isEmailOtpSent ? "Resend OTP" : "Send OTP"}
                    </Button>
                  )}
                </div>
                {isEmailOtpSent && !formData.emailVerified && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                      onChange={(e) =>
                        updateFormData({ emailOtp: e.target.value })
                      }
                    />
                    <Button
                      size="sm"
                      onClick={handleVerifyOtp}
                      loading={emailOtpLoading}
                    >
                      Verify
                    </Button>
                  </div>
                )}
                {formData.emailVerified && (
                  <p className="text-sm text-green-600 mt-2">Email Verified!</p>
                )}
                {emailOtpError && (
                  <p className="text-sm text-red-600 mt-2">{emailOtpError}</p>
                )}
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Step 2: Education
            </h3>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Education Requirements
              </label>
              {/* Dynamic rows */}
              <div className="space-y-3">
                {educationRows.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col lg:flex-row gap-3 p-3 lg-p-0 border border-gray-100 rounded-md shadow-sm lg:border-none lg:shadow-none"
                  >
                    {/* Qualification */}
                    <div className="flex-grow flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Qualification
                      </label>
                      <Select
                        allowSearch={false}
                        options={[
                          { value: "", label: "Select Qualification" },
                          ...sortedQualifications.map((q: any) => ({
                            value: String(q.qualificationId),
                            label: q.qualificationName,
                          })),
                        ]}
                        value={
                          row.qualificationId ? String(row.qualificationId) : ""
                        }
                        onChange={(value) => {
                          updateRow(idx, (r) => ({
                            ...r,
                            qualificationId: Number(value),
                            courseId: null,
                            courseName: null,
                            customCourse: null,
                            allowCustomInput: false,
                          }));
                        }}
                      />
                    </div>

                    {/* Course */}
                    <div className="flex-grow flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Course
                      </label>
                      <div className="flex items-center gap-2">
                        <Select
                          options={[
                            { value: "", label: "Select Course" },
                            ...(row.qualificationId
                              ? courseList
                                  .get(row.qualificationId)
                                  ?.filter(
                                    (c) =>
                                      !educationRows.find(
                                        (a) =>
                                          a.courseId == Number(c.value) &&
                                          a.courseId != row.courseId
                                      )
                                  ) || []
                              : []),
                          ]}
                          disabled={row.qualificationId == 0}
                          value={row.courseId ? String(row.courseId) : ""}
                          onChange={(value) => {
                            updateRow(idx, (r) => {
                              const course = resolveCourseName(
                                r.qualificationId,
                                Number(value)
                              );
                              return {
                                ...r,
                                courseId: Number(value),
                                allowCustomInput: course
                                  ? course.allowCustomInput
                                  : false,
                                customCourse: course?.allowCustomInput
                                  ? r.customCourse
                                  : null,
                                courseName: course?.courseName || null,
                              };
                            });
                          }}
                        />
                      </div>
                      {row.allowCustomInput && (
                        <input
                          type="text"
                          className="mt-2 w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter your Course name"
                          value={row.customCourse || ""}
                          onChange={(e) =>
                            updateRow(idx, (r) => ({
                              ...r,
                              customCourse: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                    {isRowComplete(row) && educationRows.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="shrink self-end text-red-600 hover:bg-red-50"
                            iconOnly
                            aria-label="Remove"
                            title="Remove"
                            onClick={() => removeRow(idx)}
                            icon={
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478 .37m-12 .92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12"
                                />
                              </svg>
                            }
                          />
                        )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Step 3: Work / Experience
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Are you Fresher or Experienced?
                </label>
                <select
                  value={formData.experienceType || ""}
                  onChange={(e) => {
                    if (e.target.value == "")
                      updateFormData({ experienceType: undefined });
                    else
                      updateFormData({
                        experienceType: e.target
                          .value as UserProfileFormData["experienceType"],
                      });
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                >
                  <option value="">Select</option>
                  <option value="fresher">Fresher</option>
                  <option value="experienced">Experienced</option>
                </select>
              </div>
              {formData.experienceType === "experienced" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Years of Experience (1-60)
                  </label>
                  <select
                    value={
                      yearsOfExperience === "" ? "" : String(yearsOfExperience)
                    }
                    onChange={(e) =>
                      setYearsOfExperience(
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  >
                    <option value="">Select years</option>
                    {Array.from({ length: 60 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? "year" : "years"}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {formData.experienceType === "experienced" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current CTC
                  </label>
                  <input
                    type="text"
                    value={formData.currentCTC || ""}
                    onChange={(e) =>
                      updateFormData({
                        ...(formData as any),
                        currentCTC: formatCurrencyINR(e.target.value),
                      } as any)
                    }
                    placeholder="e.g. ₹ 8,00,000"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  />
                </div>
              )}
              {/* For now, only show Years of Experience field for experienced users */}
            </div>
            {/* Upload section hidden as per requirement to only capture years */}
          </div>
        );
      case 4:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Step 4: Skills & Resume
            </h3>
            {/* Modern Skill Selector (fetches from allSkill.json) */}
            <SkillSelector
              label="Add Skills"
              selected={selectedSkills.map((s) => s.value)}
              onChange={(skills) => {
                console.log("skill onchange", skills);
                setSelectedSkills((prev) => {
                  const byName = new Map(prev.map((p) => [p.id, p]));
                  const next = skills.map((skill) => {
                    const key = skill.skillId;
                    const existing = byName.get(key);
                    return existing
                      ? existing
                      : {
                          value: skill,
                          id: skill.skillId,
                          name: skill.skillName,
                          level: "Beginner" as const,
                        };
                  });
                  return next;
                });
              }}
            />
            {/* Skills list - modern cards */}
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                <div className="text-sm font-semibold text-gray-700">
                  Selected Skills
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
                  {selectedSkills.length}
                </span>
              </div>
              {selectedSkills.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">
                  No skills added yet. Use the field above to add your skills.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {selectedSkills.map((s) => (
                    <li
                      key={s.id}
                      className="px-4 py-3 flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {s.name}
                        </div>
                      </div>
                      <div className="w-44">
                        <select
                          value={s.level}
                          onChange={(e) =>
                            setSelectedSkills((prev) =>
                              prev.map((x) =>
                                x.id === s.id
                                  ? { ...x, level: e.target.value as any }
                                  : x
                              )
                            )
                          }
                          className="block w-full px-3 py-2 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white text-sm"
                        >
                          <option>Beginner</option>
                          <option>Intermediate</option>
                          <option>Expert</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSkill(s.id)}
                        className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full p-2 transition-colors"
                        aria-label={`Remove ${s.name}`}
                      >
                        <svg
                          className="w-4 h-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.8}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478.37m-12 .92L6.16 19.673A2.25 2.25 0 008.403 21.75h7.514a2.25 2.25 0 002.244-2.077L19.228 5.79m-12-.92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Preferred locations - modern selector backed by allCities.json */}
            <LocationSelector
              label="Preferred Locations (Cities)"
              selected={formData.preferredLocations}
              onChange={(locs) =>
                updateFormData({ ...formData, preferredLocations: locs })
              }
            />
            {/* Resume uploader */}
            <FileUploader
              label="Upload Resume (PDF/DOC)"
              name="resume"
              accept=".pdf,.doc,.docx"
              onFileUploaded={handleAttachmentUpload}
              referenceId={user?.userId || ""}
              attachment={formData?.attachments?.find(
                (a) => a.typeId == "Resume"
              )}
              description="Attach your resume. A signed upload URL will be used."
              uploadType="Resume"
            />
          </div>
        );
      case 5:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Step 5: Complete
            </h3>
            <div className="text-center space-y-6">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  Profile Complete!
                </h4>
                <p className="text-gray-600 mb-6">
                  Your profile has been successfully completed. Review your
                  information below and submit when ready.
                </p>
              </div>

              {/* Profile Summary */}
              <div className="bg-gray-50 rounded-xl p-6 text-left">
                <h5 className="font-semibold text-gray-900 mb-4">
                  Profile Summary
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Name:</span>
                    <span className="ml-2 text-gray-900">
                      {formData.fullName || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Date of Birth:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {formData.dateOfBirth || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Gender:</span>
                    <span className="ml-2 text-gray-900">
                      {formData.gender || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Phone:</span>
                    <span className="ml-2 text-gray-900">
                      {formData.phone || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">State:</span>
                    <span className="ml-2 text-gray-900">
                      {states.find((s) => s.id === formData.currentState)
                        ?.name || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">City:</span>
                    <span className="ml-2 text-gray-900">
                      {cities.find(
                        (c) => c.id === formData.currentCity?.split(",")[0]
                      )?.name || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Experience Type:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {formData.experienceType || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Resume:</span>
                    <span className="ml-2 text-gray-900">
                      {formData.attachments.length > 0
                        ? "Uploaded"
                        : "Not uploaded"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-primary-lightGreen/10 border border-primary-lightGreen/20 rounded-xl p-4">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-primary-lightGreen mt-0.5 mr-3 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h6 className="font-medium text-primary-lightGreen">
                      Next Steps
                    </h6>
                    <p className="text-sm text-primary-lightGreen mt-1">
                      Once you submit your profile, our team will review it and
                      match you with relevant job opportunities. You'll receive
                      notifications about new matches and application updates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full py-4 sm:py-6 overflow-x-hidden no-horizontal-scroll">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      <div className="space-y-5 sm:space-y-6">
        {/* Combined Progress + Stepper - single hero box with white progress bar */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-lightGreen via-primary-teaGreen to-primary-lightGreen p-5 sm:p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-extrabold text-primary-black leading-tight">
                Complete Your Profile
              </h2>
              <p className="text-primary-black/80 text-xs sm:text-sm mt-0.5">
                Optimize your profile to get better matches
              </p>
            </div>
          </div>
          {/* Top progress bar removed; progress now shown inside the stepper box below */}

          {/* Stepper inside the same box for a single unified section */}
          <div className="mt-5 bg-white rounded-xl border border-white/40 p-4">
            <Stepper
              steps={steps}
              currentStep={currentStep}
              onStepClick={(step) => {
                if (step <= currentStep) {
                }
              }}
            />
          </div>
        </div>

        {/* Form card - stacked */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-6 xl:p-8">
          {renderStepContent()}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-8">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={isFirstStep || loading}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </span>
            </Button>

            {isLastStep ? (
              <Button
                onClick={handleSubmit}
                loading={loading}
                disabled={loading}
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Complete Profile
                </span>
              </Button>
            ) : (
              <Button onClick={handleNext} loading={loading} disabled={loading}>
                <span className="flex items-center gap-2">
                  Next
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileForm;
